import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import UserImg from "../../assets/User.png";
import FormInput from "../../components/ui/FormInput";
import { AuthAPI } from "../../services/api";

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    new_password_confirmation: "",
  });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear errors when user types
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setErrorMessage("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setErrorMessage("");
    setSuccessMessage("");

    // Frontend validation
    const validationErrors: string[] = [];

    if (!formData.current_password.trim()) {
      validationErrors.push("Current password is required");
    }

    if (!formData.new_password.trim()) {
      validationErrors.push("New password is required");
    } else if (formData.new_password.length < 6) {
      validationErrors.push("New password must be at least 6 characters long");
    }

    if (!formData.new_password_confirmation.trim()) {
      validationErrors.push("Please confirm your new password");
    }

    if (formData.new_password && formData.new_password_confirmation) {
      if (formData.new_password !== formData.new_password_confirmation) {
        validationErrors.push("New password and confirmation do not match");
      }
    }

    if (validationErrors.length > 0) {
      setErrorMessage(validationErrors.join(". ") + ".");
      setIsLoading(false);
      return;
    }

    try {
      const data = await AuthAPI.changePassword(formData);

      if (data.success) {
        setSuccessMessage("Password changed successfully!");
        setFormData({
          current_password: "",
          new_password: "",
          new_password_confirmation: "",
        });

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate("/dashboard/profile");
        }, 2000);
      } else {
        // Handle server validation errors with specific messages
        if (data.errors) {
          const errorMessages: string[] = [];

          // Extract specific error messages from the errors object
          Object.keys(data.errors).forEach((field) => {
            const fieldErrors = data.errors[field];
            if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
              errorMessages.push(...fieldErrors);
            }
          });

          if (errorMessages.length > 0) {
            setErrorMessage(errorMessages.join(". ") + ".");
          } else if (data.message) {
            setErrorMessage(data.message);
          } else {
            setErrorMessage(
              "Failed to change password. Please check your input."
            );
          }
        } else if (data.message) {
          setErrorMessage(data.message);
        } else {
          setErrorMessage("Failed to change password. Please try again.");
        }
      }
    } catch (error: any) {
      // Handle network or other errors
      setErrorMessage(
        error.message || "Failed to change password. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0">
      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
        {/* Left Column - Form */}
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6 lg:mb-8">
            <div className="w-5 sm:w-6 flex items-center justify-center">
              <img src={UserImg} alt="User Profile" className="w-full" />
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Change Password
            </h2>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-green-600 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="ml-3 text-sm text-green-800 font-medium">
                  {successMessage}
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {errorMessage}
            </div>
          )}

          {/* Form Fields */}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 mb-6">
              <FormInput
                label="Current Password"
                type="password"
                placeholder="Enter your current password"
                value={formData.current_password}
                onChange={(value) => handleChange("current_password", value)}
                required
              />

              <FormInput
                label="New Password"
                type="password"
                placeholder="Enter your new password (min 6 characters)"
                value={formData.new_password}
                onChange={(value) => handleChange("new_password", value)}
                required
              />

              <FormInput
                label="Confirm New Password"
                type="password"
                placeholder="Confirm your new password"
                value={formData.new_password_confirmation}
                onChange={(value) =>
                  handleChange("new_password_confirmation", value)
                }
                required
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-2 sm:gap-3 mt-6">
              <button
                type="button"
                onClick={() => navigate("/dashboard/profile")}
                disabled={isLoading}
                className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-3 sm:px-4 py-3 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm min-h-[44px] touch-manipulation disabled:opacity-50"
              >
                {isLoading ? "Changing..." : "Change Password"}
              </button>
            </div>
          </form>
        </div>

        {/* Right Column - Guidelines */}
        <div className="hidden lg:block w-80 space-y-4 text-left">
          <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm h-fit">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Guidelines
            </h3>

            <div className="space-y-4 text-sm text-gray-600 mb-4 sm:mb-6">
              <p>
                Fields marked <span className="text-red-500">*</span> are
                required.
              </p>
              <div className="space-y-2">
                <p className="font-medium text-gray-900">
                  Password Requirements:
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>At least 6 characters long</li>
                  <li>Mix uppercase and lowercase letters</li>
                  <li>Include numbers and special characters</li>
                  <li>Different from your current password</li>
                </ul>
              </div>
            </div>

            <div className="p-3 rounded-lg border bg-blue-50 border-blue-200">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">ℹ</span>
                </div>
                <div className="text-sm">
                  <div className="font-medium text-blue-800">
                    Keep your account secure
                  </div>
                  <div className="text-blue-600">
                    Change your password regularly and don't share it with
                    anyone.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Guidelines Popup Trigger */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <button
          onClick={() => {
            const content = `
              Password Requirements:
              • At least 6 characters long
              • Mix uppercase and lowercase letters
              • Include numbers and special characters
              • Different from your current password
            `;
            alert(content);
          }}
          className="w-12 h-12 bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center touch-manipulation"
          aria-label="Show guidelines"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </button>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            <p className="text-gray-800 font-medium">Changing password...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangePassword;
